import { CircleDot, Hammer, Home, Layers, Lightbulb, PanelTop, PlugZap, Wrench, Zap } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const iconMap = { Zap, Home, Hammer, Lightbulb, CircleDot, Layers, PanelTop, Wrench, PlugZap };

// Bento grid classes per index (mobile stacks, lg follows bento pattern)
const bento = [
  "lg:col-span-2 lg:row-span-2",   // EV - big
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-2 lg:row-span-1",   // Lighting - wide
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-2 lg:row-span-1",   // Installations - wide
];

const heroImages = {
  ev: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxFViUyMGNoYXJnZXIlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzgzOTg2MDg1fDA&ixlib=rb-4.1.0&q=85",
  lighting: "https://images.unsplash.com/photo-1763718476503-8d0b81eb1746?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZWNlc3NlZCUyMGxpZ2h0aW5nJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzOTg2MDg1fDA&ixlib=rb-4.1.0&q=85",
};

export default function Services() {
  const { settings } = useSettings();
  const services = settings?.services || [];

  return (
    <section id="services" className="relative py-24 lg:py-32 bg-[#050505] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-yellow-400 font-bold mb-4">
              ⚡ What We Do
            </div>
            <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
              Every wire, every outlet,<br />
              every <span className="text-yellow-400">connection</span>.
            </h2>
          </div>
          <p className="text-white/60 text-base lg:text-lg max-w-md leading-relaxed">
            A full-service electrical contractor for residential and light commercial jobs across the DMV.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[220px] gap-4">
          {services.map((svc, idx) => {
            const Icon = iconMap[svc.icon] || Zap;
            const layout = bento[idx] || "lg:col-span-1 lg:row-span-1";
            const isFeatured = idx === 0;
            const heroImg = svc.id === "ev" ? heroImages.ev : svc.id === "lighting" || svc.id === "recessed" ? heroImages.lighting : null;

            return (
              <div
                key={svc.id}
                data-testid={`service-card-${svc.id}`}
                className={`group relative overflow-hidden rounded-xl border border-white/10 bg-[#0d0d0f] p-6 lg:p-7 transition-all duration-500 hover:border-yellow-400/40 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(250,204,21,0.15)] ${layout}`}
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                {isFeatured && heroImg && (
                  <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                    <img src={heroImg} alt={svc.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-[#0d0d0f]/70 to-transparent" />
                  </div>
                )}
                <div className="relative z-10 h-full flex flex-col">
                  <div className={`w-12 h-12 rounded-md flex items-center justify-center mb-5 transition-all ${isFeatured ? "bg-yellow-400 text-black" : "bg-white/5 text-yellow-400 group-hover:bg-yellow-400 group-hover:text-black"}`}>
                    <Icon className="w-5.5 h-5.5" strokeWidth={2} />
                  </div>
                  <h3 className={`font-display font-semibold text-white tracking-tight leading-tight mb-2 ${isFeatured ? "text-2xl lg:text-3xl" : "text-lg lg:text-xl"}`}>
                    {svc.title}
                  </h3>
                  <p className="text-white/55 text-sm leading-relaxed">
                    {svc.description}
                  </p>
                  <div className="mt-auto pt-4 flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-yellow-400/0 group-hover:text-yellow-400 transition-all">
                    <span className="font-bold">Request Service</span>
                    <span>→</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
